<template>
  <div>
    <material-card
      icon="mdi-flag-variant"
      icon-small
      color="primary"
      title="Feature Management"
    >
      <v-card-text>
        <v-tabs
          v-model="mainTab"
          class="mt-2"
        >
          <v-tab>Feature Definitions</v-tab>
          <v-tab>Company Features</v-tab>
        </v-tabs>

        <v-tabs-items v-model="mainTab">
          <!-- Feature Definitions -->
          <v-tab-item>
            <v-row
              class="mt-4"
              align="center"
            >
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-text-field
                  v-model="featureSearch"
                  append-icon="mdi-magnify"
                  label="Search features"
                  clearable
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-select
                  v-model="featureFilter.isActive"
                  :items="activeFilterItems"
                  label="Status"
                  clearable
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                md="5"
                class="text-md-right"
              >
                <app-btn
                  class="mr-2"
                  @click="loadFeatures"
                >
                  <v-icon>mdi-refresh</v-icon> Refresh
                </app-btn>
                <app-btn
                  class="mr-2"
                  color="secondary"
                  @click="startPicker"
                >
                  <v-icon>mdi-cursor-default-click</v-icon> Pick Element
                </app-btn>
                <app-btn @click="openFeatureDialog()">
                  <v-icon>mdi-plus</v-icon> New Feature
                </app-btn>
              </v-col>
            </v-row>

            <v-divider class="mt-3" />

            <v-alert
              type="info"
              text
              dense
              class="mt-3"
            >
              "Pick Element" lets you click any button/screen in the app that a developer has marked with
              <code>v-feature-key</code> and instantly attach it to a feature. Unmarked elements can't be picked yet.
            </v-alert>

            <v-data-table
              :headers="featureHeaders"
              :items="filteredFeatures"
              :loading="featuresLoading"
              class="responsive-data-table"
              item-key="id"
              :items-per-page="15"
              :footer-props="{
                showFirstLastPage: true,
                'items-per-page-options': [10, 15, 25, 50],
              }"
            >
              <template v-slot:[`item.image`]="{ item }">
                <v-icon v-if="isMdiIcon(item.image)">
                  {{ item.image }}
                </v-icon>
                <v-avatar
                  v-else-if="item.image"
                  size="24"
                >
                  <img
                    :src="item.image"
                    :alt="item.name"
                  >
                </v-avatar>
                <span
                  v-else
                  class="grey--text"
                >&mdash;</span>
              </template>

              <template v-slot:[`item.key`]="{ item }">
                <code class="feature-key">{{ item.key }}</code>
              </template>

              <template v-slot:[`item.defaultEnabled`]="{ item }">
                <v-chip
                  small
                  :color="item.defaultEnabled ? 'success' : 'grey'"
                  text-color="white"
                >
                  {{ item.defaultEnabled ? 'On' : 'Off' }}
                </v-chip>
              </template>

              <template v-slot:[`item.isActive`]="{ item }">
                <v-switch
                  :input-value="item.isActive"
                  hide-details
                  class="ma-0 pa-0"
                  @click.stop="toggleFeatureStatus(item)"
                />
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="openFeatureDialog(item)"
                    >
                      <v-icon small>
                        mdi-pencil
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Edit feature</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="error"
                      v-bind="attrs"
                      v-on="on"
                      @click="deleteFeatureDialog.show(item)"
                    >
                      <v-icon small>
                        mdi-delete
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Delete feature</span>
                </v-tooltip>
              </template>
            </v-data-table>

            <user-prompt
              v-model="deleteFeatureDialog"
              @confirm="deleteFeature"
            />
          </v-tab-item>

          <!-- Company Features -->
          <v-tab-item>
            <v-row
              class="mt-4"
              align="center"
            >
              <v-col
                cols="12"
                sm="8"
                md="6"
              >
                <v-autocomplete
                  v-model="selectedCompanyId"
                  :items="companies"
                  item-text="pathStr"
                  item-value="id"
                  label="Company"
                  clearable
                  :loading="companiesLoading"
                  @focus="ensureCompanyDropdownCompanies"
                  @change="loadCompanyFeatures"
                />
              </v-col>
              <v-col
                cols="12"
                md="6"
                class="text-md-right"
              >
                <app-btn
                  :disabled="!selectedCompanyId"
                  @click="loadCompanyFeatures"
                >
                  <v-icon>mdi-refresh</v-icon> Refresh
                </app-btn>
              </v-col>
            </v-row>

            <v-divider class="mt-3" />

            <v-alert
              v-if="!selectedCompanyId"
              type="info"
              text
              dense
              class="mt-4"
            >
              Select a company to view and manage its enabled features.
            </v-alert>

            <template v-else>
              <div
                v-if="companyFeaturesLoading"
                class="text-center mt-6"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                />
              </div>

              <template v-else>
                <div
                  v-for="group in groupedCompanyFeatures"
                  :key="group.category"
                  class="mt-4"
                >
                  <div class="text-subtitle-1 font-weight-medium grey--text text--darken-1">
                    {{ group.category }}
                  </div>
                  <v-divider class="mb-2" />

                  <v-row
                    v-for="feature in group.items"
                    :key="feature.key"
                    align="center"
                    dense
                    class="feature-row"
                  >
                    <v-col
                      cols="12"
                      sm="6"
                    >
                      <div class="font-weight-medium">
                        {{ feature.name }}
                      </div>
                      <div class="text-caption grey--text">
                        <code class="feature-key">{{ feature.key }}</code>
                        <span v-if="feature.description"> &mdash; {{ feature.description }}</span>
                      </div>
                    </v-col>
                    <v-col
                      cols="6"
                      sm="3"
                    >
                      <v-switch
                        :input-value="companyFeatureState[feature.key]"
                        :label="companyFeatureState[feature.key] ? 'Enabled' : 'Disabled'"
                        hide-details
                        class="ma-0 pa-0"
                        :disabled="!feature.isActive"
                        @click.stop="toggleCompanyFeature(feature)"
                      />
                    </v-col>
                    <v-col
                      cols="6"
                      sm="3"
                      class="text-right"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            v-bind="attrs"
                            v-on="on"
                            @click="resetCompanyFeature(feature.key)"
                          >
                            <v-icon small>
                              mdi-restore
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>Reset to default ({{ feature.defaultEnabled ? 'On' : 'Off' }})</span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </div>
              </template>
            </template>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </material-card>

    <!-- Feature dialog -->
    <v-dialog
      v-model="featureDialog"
      max-width="620px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ featureItem.id ? 'Edit Feature' : 'New Feature' }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="featureForm">
            <v-row class="mt-2">
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="featureItem.key"
                  label="Key"
                  placeholder="navigation.devices"
                  :disabled="!!featureItem.id"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="featureItem.name"
                  label="Name"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="featureItem.image"
                  label="Image / Icon"
                  placeholder="mdi-flag-variant or image URL"
                  hint="Auto-filled from the picked element's icon; edit or clear as needed."
                  persistent-hint
                >
                  <template v-slot:prepend>
                    <v-icon v-if="isMdiIcon(featureItem.image)">
                      {{ featureItem.image }}
                    </v-icon>
                    <v-avatar
                      v-else-if="featureItem.image"
                      size="24"
                    >
                      <img
                        :src="featureItem.image"
                        alt=""
                      >
                    </v-avatar>
                    <v-icon
                      v-else
                      color="grey lighten-1"
                    >
                      mdi-image-off-outline
                    </v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="featureItem.category"
                  label="Category"
                  placeholder="navigation"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="featureItem.requiredPermission"
                  label="Required Permission"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model.number="featureItem.sortOrder"
                  label="Sort Order"
                  type="number"
                />
              </v-col>
              <v-col
                cols="12"
                sm="3"
              >
                <v-switch
                  v-model="featureItem.defaultEnabled"
                  label="Default Enabled"
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                sm="3"
              >
                <v-switch
                  v-model="featureItem.isActive"
                  label="Active"
                  hide-details
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="featureItem.description"
                  label="Description"
                  rows="2"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            :disabled="savingFeature"
            @click="featureDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            :loading="savingFeature"
            @click="saveFeature"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import store from '@/store'
  import UserPrompt from './components/custom/UserPrompt.vue'

  export default {
    name: 'FeatureManagement',

    components: { UserPrompt },

    data: () => ({
      mainTab: 0,
      rules: {
        required: (value) => (value !== null && value !== undefined && value !== '') || 'Required.',
      },
      activeFilterItems: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],

      featuresLoading: false,
      featureItems: [],
      featureSearch: '',
      featureFilter: {
        isActive: undefined,
      },
      featureHeaders: [
        { text: 'Image', value: 'image', sortable: false },
        { text: 'Key', value: 'key' },
        { text: 'Name', value: 'name' },
        { text: 'Category', value: 'category' },
        { text: 'Default', value: 'defaultEnabled', sortable: false },
        { text: 'Active', value: 'isActive', sortable: false },
        { text: 'Sort Order', value: 'sortOrder' },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ],
      featureDialog: false,
      savingFeature: false,
      featureItem: {},
      defaultFeatureItem: {
        id: undefined,
        key: '',
        name: '',
        description: '',
        image: '',
        category: '',
        defaultEnabled: false,
        isActive: true,
        requiredPermission: '',
        sortOrder: 0,
      },
      deleteFeatureDialog: null,

      companiesLoading: false,
      companiesLoadedForDropdown: false,
      selectedCompanyId: undefined,
      companyFeaturesLoading: false,
      companyFeaturesVersion: 0,
      companyFeatureState: {},
    }),

    computed: {
      companies () {
        return store.getters['company/getCompanies']
      },

      filteredFeatures () {
        const search = (this.featureSearch || '').toLowerCase()
        return this.featureItems.filter(item => {
          if (this.featureFilter.isActive !== undefined && this.featureFilter.isActive !== null && item.isActive !== this.featureFilter.isActive) {
            return false
          }
          if (!search) {
            return true
          }
          return (item.key || '').toLowerCase().includes(search) ||
            (item.name || '').toLowerCase().includes(search) ||
            (item.category || '').toLowerCase().includes(search)
        })
      },

      groupedCompanyFeatures () {
        const groups = {}
        this.featureItems
          .slice()
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
          .forEach(feature => {
            const category = feature.category || 'General'
            groups[category] ??= []
            groups[category].push(feature)
          })

        return Object.keys(groups).sort().map(category => ({
          category,
          items: groups[category],
        }))
      },
    },

    async created () {
      if (!this.$store.getters['authUser/isSuperUser']) {
        this.$toast.error('You do not have access to Feature Management.', 'Error', { position: 'topRight' })
        this.$router.replace('/devices')
        return
      }

      this.featureItem = Object.assign({}, this.defaultFeatureItem)
      this.$signalHub.$on('feature-picker-selected', this.onPickerElementSelected)

      await this.loadFeatures()

      // A pick made on another page lands here via Vuex (it's set before the
      // navigation that mounts this component, so a fire-and-forget signal
      // emitted at pick time would already have nobody listening).
      const pending = await this.$store.dispatch('companyFeatures/consumePendingPick')
      if (pending) {
        this.onPickerElementSelected(pending)
      }
    },

    beforeDestroy () {
      this.$signalHub.$off('feature-picker-selected', this.onPickerElementSelected)
    },

    methods: {
      startPicker () {
        this.$store.dispatch('companyFeatures/startPicker', '/feature-management')
        this.notifySuccess('Picker mode enabled. Browse to a screen and click a marked element.')
      },

      isMdiIcon (value) {
        return typeof value === 'string' && value.startsWith('mdi-')
      },

      humanizeKey (value) {
        return String(value || '')
          .replace(/[._-]+/g, ' ')
          .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
          .replace(/\s+/g, ' ')
          .trim()
          .replace(/\b\w/g, char => char.toUpperCase())
      },

      onPickerElementSelected ({ key, label, icon }) {
        this.$store.commit('companyFeatures/setPendingPick', null)
        this.mainTab = 0
        const existing = this.featureItems.find(item => item.key === key)

        if (existing) {
          this.openFeatureDialog(existing)
          this.notifySuccess(`Editing existing feature "${existing.key}".`)
          return
        }

        this.openFeatureDialog()
        this.featureItem.key = key
        this.featureItem.name = this.humanizeKey(label || key)
        this.featureItem.image = icon || ''
        this.notifySuccess(`New feature pre-filled from picked element "${key}".`)
      },

      notifyError (error, fallback) {
        this.$toast.error(
          error?.response?.data?.message || error?.message || fallback,
          'Error',
          { position: 'topRight' },
        )
      },

      notifySuccess (message) {
        this.$toast.success(message, 'Success', { position: 'topRight' })
      },

      isFailedResponse (responseData) {
        return responseData?.success === false
      },

      // Company dropdown -----------------------------------------------
      async ensureCompanyDropdownCompanies () {
        if (this.companiesLoading || this.companiesLoadedForDropdown) {
          return
        }

        this.companiesLoading = true
        try {
          const queuedIds = new Set()
          const loadedIds = new Set()
          const rootCompanies = await store.dispatch('company/loadCompanies', 0)
          const queue = rootCompanies.map(company => company.id).filter(Boolean)

          queue.forEach(id => queuedIds.add(id))

          while (queue.length && loadedIds.size < 500) {
            const companyId = queue.shift()

            if (!companyId || loadedIds.has(companyId)) {
              continue
            }

            loadedIds.add(companyId)
            const existingCompany = store.state.company.companies[companyId]
            const existingChildren = existingCompany?.children || []
            const children = existingChildren.length
              ? existingChildren
              : await store.dispatch('company/loadCompanies', companyId)

            children.forEach(child => {
              if (child?.id && !queuedIds.has(child.id) && !loadedIds.has(child.id)) {
                queuedIds.add(child.id)
                queue.push(child.id)
              }
            })
          }

          this.companiesLoadedForDropdown = true
        } catch (error) {
          this.notifyError(error, 'Could not load all companies.')
        } finally {
          this.companiesLoading = false
        }
      },

      // Feature definitions ----------------------------------------------
      async loadFeatures () {
        this.featuresLoading = true
        try {
          const response = await this.$http.get('features')
          const data = response.data?.data ?? response.data
          this.featureItems = Array.isArray(data) ? data : (data?.items || [])
        } catch (error) {
          this.notifyError(error, 'Could not load features.')
        } finally {
          this.featuresLoading = false
        }
      },

      openFeatureDialog (item) {
        this.featureItem = Object.assign({}, this.defaultFeatureItem, item || {})
        this.featureDialog = true
        if (this.$refs.featureForm) {
          this.$refs.featureForm.resetValidation()
        }
      },

      async saveFeature () {
        if (!this.$refs.featureForm.validate()) {
          return
        }

        const payload = {
          key: String(this.featureItem.key || '').trim().toLowerCase(),
          name: this.featureItem.name,
          description: this.featureItem.description || '',
          image: this.featureItem.image || '',
          category: this.featureItem.category || '',
          defaultEnabled: !!this.featureItem.defaultEnabled,
          isActive: !!this.featureItem.isActive,
          requiredPermission: this.featureItem.requiredPermission || '',
          sortOrder: Number(this.featureItem.sortOrder) || 0,
        }

        this.savingFeature = true
        try {
          const response = this.featureItem.id
            ? await this.$http.put(`features/${this.featureItem.id}`, payload)
            : await this.$http.post('features', payload)

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not save feature.')
            return
          }

          this.notifySuccess(response.data?.message || 'Feature saved successfully.')
          this.featureDialog = false
          this.loadFeatures()
        } catch (error) {
          this.notifyError(error, 'Could not save feature.')
        } finally {
          this.savingFeature = false
        }
      },

      toggleFeatureStatus (item) {
        item.isActive = !item.isActive
        this.updateFeatureStatus(item)
      },

      async updateFeatureStatus (item) {
        try {
          const response = await this.$http.patch(`features/${item.id}/status`, {
            isActive: item.isActive,
          })

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not update feature status.')
            this.loadFeatures()
            return
          }

          this.notifySuccess('Feature status updated.')
        } catch (error) {
          this.notifyError(error, 'Could not update feature status.')
          this.loadFeatures()
        }
      },

      async deleteFeature (item) {
        try {
          const response = await this.$http.delete(`features/${item.id}`)

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not delete feature.')
            return
          }

          this.notifySuccess(response.data?.message || 'Feature deleted successfully.')
          this.loadFeatures()
        } catch (error) {
          this.notifyError(error, 'Could not delete feature.')
        }
      },

      // Company features ---------------------------------------------------
      async loadCompanyFeatures () {
        if (!this.selectedCompanyId) {
          this.companyFeatureState = {}
          return
        }

        this.companyFeaturesLoading = true
        try {
          const response = await this.$http.get(`company-features/${this.selectedCompanyId}`)
          const data = response.data?.data ?? response.data

          this.companyFeaturesVersion = data?.version || 0
          const enabledMap = this.buildEnabledMap(data)

          const state = {}
          this.featureItems.forEach(feature => {
            const key = String(feature.key || '').toLowerCase()
            state[feature.key] = Object.prototype.hasOwnProperty.call(enabledMap, key)
              ? !!enabledMap[key]
              : !!feature.defaultEnabled
          })
          this.companyFeatureState = state
        } catch (error) {
          this.notifyError(error, 'Could not load company features.')
        } finally {
          this.companyFeaturesLoading = false
        }
      },

      // The endpoint's resolved-per-item `items[].enabled` is the source of
      // truth (it already accounts for role/permission checks server-side);
      // the flat `features` map is only used as a fallback if `items` is
      // absent, since some responses (e.g. GET /me) may only include one.
      buildEnabledMap (data) {
        const enabledMap = {}
        const items = Array.isArray(data?.items) ? data.items : []

        items.forEach(item => {
          if (!item?.key) {
            return
          }
          const key = String(item.key).toLowerCase()
          const enabled = item.enabled ?? item.companyEnabled ?? item.defaultEnabled ?? false
          enabledMap[key] = !!enabled
        })

        if (!items.length && data?.features) {
          Object.keys(data.features).forEach(key => {
            enabledMap[String(key).toLowerCase()] = !!data.features[key]
          })
        }

        return enabledMap
      },

      toggleCompanyFeature (feature) {
        const next = !this.companyFeatureState[feature.key]
        this.$set(this.companyFeatureState, feature.key, next)
        this.setCompanyFeature(feature.key, next)
      },

      async setCompanyFeature (featureKey, enabled) {
        try {
          const response = await this.$http.put(
            `company-features/${this.selectedCompanyId}/${featureKey}`,
            // GET .../me returns the raw override as `companyEnabled` (distinct
            // from the resolved `enabled`). Sending both names covers whichever
            // one the write endpoint actually expects, since a mismatch here
            // would silently no-op the override and revert to defaultEnabled.
            { enabled: !!enabled, companyEnabled: !!enabled, isEnabled: !!enabled },
          )

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not update company feature.')
            this.loadCompanyFeatures()
            return
          }

          this.notifySuccess('Company feature updated.')
          // Re-sync with the server's resolved value rather than trusting the
          // optimistic local flip, so a silent persistence failure (wrong
          // payload shape, permission denial, etc.) shows up immediately.
          this.loadCompanyFeatures()
        } catch (error) {
          this.notifyError(error, 'Could not update company feature.')
          this.loadCompanyFeatures()
        }
      },

      async resetCompanyFeature (featureKey) {
        try {
          const response = await this.$http.delete(`company-features/${this.selectedCompanyId}/${featureKey}`)

          if (this.isFailedResponse(response.data)) {
            this.notifyError(null, response.data?.message || 'Could not reset company feature.')
            return
          }

          this.notifySuccess('Company feature reset to default.')
          this.loadCompanyFeatures()
        } catch (error) {
          this.notifyError(error, 'Could not reset company feature.')
        }
      },
    },
  }
</script>

<style scoped>
  .feature-key {
    background: #f5f5f5;
    border-radius: 4px;
    box-shadow: none;
    font-size: 12px;
    padding: 2px 6px;
  }

  .feature-row {
    padding: 4px 0;
  }
</style>
